import { inject, injectable } from "@launchtray/tsyringe-async";
import { INetworkClient } from "../networking/INetworkClient";
import { CreateBluesnapWalletParamsDto } from "../networking/data/requests/CreateBluesnapWalletParamsDto";
import { ProcessBluesnapOrderParamsDto } from "../networking/data/requests/ProcessBluesnapOrderParamsDto";
import { WalletType } from "./WalletType";
import { IApplePayApi } from "./IApplePayApi";
import { PromiseCompletionSource } from "../../shared/async/PromiseCompletionSource";
import { PurchaseItemData } from "./PurchaseItemData";
import { PaymentStatus } from "./PaymentStatus";
import { IAnalyticProviderWrapper } from "../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../analytics/events/VirtualScreenEvent";
import { ILogger } from "../logger/ILogger";

@injectable()
export class ApplePayApi implements IApplePayApi {
    private static readonly APPLE_PAY_DEFAULT_SUPPORTED_VERSION = 14;
    private static readonly APPLE_PAY_MIN_SUPPORTED_VERSION = 1;
    private static readonly PRODUCTION_MERCHANT_ID = "1324330";

    public get isApplePayAvailable(): boolean {
        return (window as any).ApplePaySession && ApplePaySession.canMakePayments();
    }
    private readonly networkClient: INetworkClient;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly logger: ILogger;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IAnalyticProviderWrapper") analyticsProvider: IAnalyticProviderWrapper,
        @inject("ILogger") logger: ILogger
    ) {
        this.networkClient = networkClient;
        this.analyticsProvider = analyticsProvider;
        this.logger = logger;
    }

    public startPaymentSession(purchaseItem: PurchaseItemData, paymentSessionCS: PromiseCompletionSource<PaymentStatus>): void {
        const haveEventName = "Buying user has CC on Apple pay";
        const notHaveEventName = "Buying user DOESN'T have CC on Apple pay";
        if (this.applePayAvailableWithActiveCardEvent(haveEventName, notHaveEventName) == false) {
            paymentSessionCS?.complete(PaymentStatus.FAILURE);
            return;
        }

        const request: ApplePayJS.ApplePayPaymentRequest = {
            countryCode: "US",
            currencyCode: purchaseItem.currency,
            total: { label: purchaseItem.source, amount: purchaseItem.amount.toString(), type: "final" },
            supportedNetworks: ["amex", "discover", "jcb", "masterCard", "visa"],
            merchantCapabilities: ["supports3DS"]
        };
        const session = new ApplePaySession(this.getSupportedApiVersion(), request);

        const network = this.networkClient;
        const analyticsProvider = this.analyticsProvider;
        session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
            const validationURL = event.validationURL;
            const bluesnapWalletParams: CreateBluesnapWalletParamsDto = {
                walletType: WalletType.APPLE_PAY,
                validationUrl: validationURL,
                domainName: window.location.hostname
            };
            try {
                const validateTokenResult = await network.createBluesnapWallet(bluesnapWalletParams);
                const decodedToken = atob(validateTokenResult.walletToken);
                session.completeMerchantValidation(JSON.parse(decodedToken));
            } catch (error: unknown) {
                this.logger.error("ApplePayApiError", error);
                paymentSessionCS?.complete(PaymentStatus.FAILURE);
            }
        };

        session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
            try {
                const paymentToken = event.payment;
                const processBluesnapOrderParams: ProcessBluesnapOrderParamsDto = {
                    storeItemId: purchaseItem.storeItemId,
                    currency: purchaseItem.currency,
                    isForGoldenWheel: purchaseItem.isForGoldenWheel,
                    storeOfferId: purchaseItem.storeOfferId,
                    source: purchaseItem.source,
                    personalOfferId: purchaseItem.personalOfferId,
                    walletType: WalletType.APPLE_PAY,
                    encodedPaymentToken: btoa(JSON.stringify(paymentToken))
                };
                const fbEventId = await network.createBlusenapPayment(processBluesnapOrderParams);
                session.completePayment(ApplePaySession.STATUS_SUCCESS);
                paymentSessionCS?.complete(PaymentStatus.SUCCESS);
            } catch (error: unknown) {
                this.logger.error("ApplePayApiError", error);
                session.completePayment(ApplePaySession.STATUS_FAILURE);
                paymentSessionCS?.complete(PaymentStatus.FAILURE);
            }
        };
        session.oncancel = (_event: ApplePayJS.Event) => {
            paymentSessionCS?.complete(PaymentStatus.CANCEL);
            analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Buying user closed Apple Pay popup", analyticsProvider.currentEventIndex));
        };
        session.begin();
    }

    public applePayAvailableWithActiveCardEvent(haveEventName: string, notHaveEventName: string): boolean {
        if (!this.isApplePayAvailable) {
            this.logger.info("ApplePayApi is not available.");
            return false;
        }

        const analyticsProvider = this.analyticsProvider;
        const checkActiveCardPromise = ApplePaySession.canMakePaymentsWithActiveCard(
            `${ApplePayApi.PRODUCTION_MERCHANT_ID}-${window.location.hostname}`
        );
        checkActiveCardPromise.then((canMakePaymentsWithActiveCard) => {
            const eventName = canMakePaymentsWithActiveCard ? haveEventName : notHaveEventName;
            analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(eventName, analyticsProvider.currentEventIndex));
        });
        return true;
    }

    private getSupportedApiVersion(): number {
        if (!this.isApplePayAvailable) {
            throw new Error("ApplePayApi is not available.");
        }

        let checkedVersion = ApplePayApi.APPLE_PAY_DEFAULT_SUPPORTED_VERSION;
        while (checkedVersion >= ApplePayApi.APPLE_PAY_MIN_SUPPORTED_VERSION) {
            if (ApplePaySession.supportsVersion(checkedVersion)) {
                this.logger.info(`Supported version: ${checkedVersion}`);
                return checkedVersion;
            } else {
                checkedVersion--;
            }
        }
        return ApplePayApi.APPLE_PAY_DEFAULT_SUPPORTED_VERSION;
    }
}
